/**
 * CALIDAD — PIEZAS COMPARTIDAS (FASE 7.1)
 *
 * Incidencias, ficha de calidad de una NC (análisis de causa, acciones
 * correctivas, incidencias vinculadas, línea de tiempo) y el acceso
 * «Reportar incidencia» desde una OT o una línea.
 *
 * Igual que `no-conformidades.tsx`: los botones que se muestran son sólo un
 * reflejo de las máquinas de estado del servidor (0054) — nunca la
 * autoridad. Tras cada escritura se relee del servidor; nada se simula.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  api,
  type AccionCorrectiva,
  type ComentarioNc,
  type Centro,
  type Cliente,
  type EstadoAccionCorrectiva,
  type EstadoIncidencia,
  type EventoCalidad,
  type FichaCalidadNc,
  type GravedadNc,
  type IncidenciaFila,
  type OtDetalle,
  type PaginaIncidencias,
  type ResponsableCandidato,
} from '../api.ts';
import { useSesion } from '../contexto.tsx';
import { estadoAccionCorrectiva, estadoIncidenciaCalidad } from '../estados.ts';
import { Cargando, ConfirmDialog, Error as ErrorCaja, Estado, fecha, fechaHora, useDatos, Vacio } from './base.tsx';
import { Evidencias } from './evidencias.tsx';

// ------------------------------------------------------------ etiquetas

export const ETIQUETAS_NIVEL: Record<string, string> = { LEVE: 'Leve', GRAVE: 'Grave', CRITICA: 'Crítica' };

//  UX-1C: estados de incidencias y acciones correctivas, del mapa central (`estados.ts`).
export function etiquetaEstadoIncidencia(e: string): string {
  return estadoIncidenciaCalidad(e).label;
}

export function EstadoIncidenciaEtiqueta({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoIncidenciaCalidad(estado)} />;
}

export function EstadoAccionEtiqueta({ estado }: { estado: string }): ReactNode {
  return <Estado d={estadoAccionCorrectiva(estado)} />;
}

/** F11.3: `null` = gravedad oficial todavía sin clasificar por la empresa (NO es un estado). */
export function NivelEtiqueta({ nivel }: { nivel: string | null }): ReactNode {
  if (nivel === null) return <span className="etiqueta aviso">Pendiente de clasificar</span>;
  const variante = nivel === 'CRITICA' ? 'error' : nivel === 'GRAVE' ? 'aviso' : 'neutra';
  return <span className={`etiqueta ${variante}`}>{ETIQUETAS_NIVEL[nivel] ?? nivel}</span>;
}

/** F11.3: lo que el CLIENTE percibe al abrir su NC (no es la gravedad oficial). */
const ETIQUETAS_IMPACTO: Record<string, string> = { BAJO: 'Bajo', MEDIO: 'Medio', ALTO: 'Alto' };
const ETIQUETAS_URGENCIA: Record<string, string> = { BAJA: 'Baja', MEDIA: 'Media', ALTA: 'Alta' };
export function textoPercepcionCliente(impacto: string | null | undefined, urgencia: string | null | undefined): string | null {
  const partes = [
    impacto != null ? `impacto ${(ETIQUETAS_IMPACTO[impacto] ?? impacto).toLowerCase()}` : null,
    urgencia != null ? `urgencia ${(ETIQUETAS_URGENCIA[urgencia] ?? urgencia).toLowerCase()}` : null,
  ].filter((x): x is string => x !== null);
  return partes.length === 0 ? null : partes.join(' · ');
}

/** Espejo de TRANSICIONES_INCIDENCIA (backend) — sólo para decidir qué botón ofrecer. */
export const SIGUIENTES_INCIDENCIA: Record<EstadoIncidencia, readonly EstadoIncidencia[]> = {
  ABIERTA: ['EN_ANALISIS', 'RESUELTA', 'CANCELADA'],
  EN_ANALISIS: ['RESUELTA', 'CANCELADA'],
  RESUELTA: ['CERRADA', 'EN_ANALISIS'],
  CERRADA: [],
  CANCELADA: [],
};

const SIGUIENTES_ACCION: Record<EstadoAccionCorrectiva, readonly EstadoAccionCorrectiva[]> = {
  PENDIENTE: ['EN_CURSO', 'COMPLETADA', 'CANCELADA'],
  EN_CURSO: ['COMPLETADA', 'CANCELADA'],
  COMPLETADA: ['EN_CURSO'],
  CANCELADA: [],
};

const VERBO_ACCION: Record<EstadoAccionCorrectiva, string> = {
  PENDIENTE: 'Pendiente',
  EN_CURSO: 'Empezar',
  COMPLETADA: 'Completar',
  CANCELADA: 'Cancelar acción',
};

// ------------------------------------------------------------ responsables

export function useResponsables(): ResponsableCandidato[] {
  const { puede } = useSesion();
  const habilitado = puede('incidencias:gestionar') || puede('nc:tratar');
  const estado = useDatos(
    () => (habilitado ? api.get<{ filas: ResponsableCandidato[] }>('/api/calidad/responsables') : Promise.resolve({ filas: [] })),
    [habilitado],
  );
  return estado.datos?.filas ?? [];
}

export function SelectorResponsable({
  id,
  valor,
  onChange,
  responsables,
  vacio = 'Sin asignar',
}: {
  id: string;
  valor: string;
  onChange: (v: string) => void;
  responsables: readonly ResponsableCandidato[];
  vacio?: string | null;
}): ReactNode {
  return (
    <select id={id} value={valor} onChange={(e) => onChange(e.target.value)}>
      {vacio !== null && <option value="">{vacio}</option>}
      {vacio === null && valor === '' && <option value="">Elige responsable…</option>}
      {responsables.map((r) => (
        <option key={r.id} value={r.id}>
          {r.nombre}
        </option>
      ))}
    </select>
  );
}

// ------------------------------------------------------------ línea de tiempo

const NOMBRE_CAMPO: Record<string, string> = {
  descripcion: 'descripción',
  gravedad: 'nivel',
  idResponsable: 'responsable',
  fechaObjetivo: 'fecha objetivo',
  visibleCliente: 'visible para el cliente',
  analisisCausa: 'análisis de causa',
};

/** FASE 7.2: nunca «en_analisis» ni códigos en crudo en la línea de tiempo. */
const NOMBRE_ESTADO: Record<string, string> = {
  ABIERTA: 'abierta', EN_ANALISIS: 'en análisis', RESUELTA: 'resuelta', CERRADA: 'cerrada', CANCELADA: 'cancelada',
  EN_TRATAMIENTO: 'en tratamiento', ANULADA: 'anulada', PENDIENTE: 'pendiente', EN_CURSO: 'en curso', COMPLETADA: 'completada',
};
export function nombreEstado(e: string | null): string {
  return e === null ? '' : NOMBRE_ESTADO[e] ?? e.toLowerCase().replaceAll('_', ' ');
}

function textoEvento(ev: EventoCalidad, sujetoPagina?: EventoCalidad['sujeto']): string {
  //  F11.2: comentarios y cambios de visibilidad, dichos en claro (nunca «editada (visibilidad)»).
  if (ev.tipo === 'COMENTARIO') {
    const c = ev.cambios as unknown as { visibleCliente?: boolean; deCliente?: boolean } | null;
    return c?.deCliente === true ? 'Comentario del cliente' : c?.visibleCliente === true ? 'Comentario para el cliente' : 'Comentario interno';
  }
  const vis = ev.tipo === 'EDITADA' ? (ev.cambios?.['visibilidad'] ?? ev.cambios?.['visibleCliente']) : undefined;
  if (vis !== undefined && Object.keys(ev.cambios ?? {}).length === 1) {
    const ahora = vis.despues === true || vis.despues === 'CLIENTE';
    if (ev.sujeto === 'EVIDENCIA') return `Evidencia ${ev.referencia ?? ''} ${ahora ? 'compartida con el cliente' : 'pasada a solo interna'}`;
    if (ev.sujeto === 'ACCION') return `Acción correctiva ${ahora ? 'compartida con el cliente' : 'retirada al cliente'}`;
    if (ev.sujeto === 'NC') return `No conformidad ${ahora ? 'publicada al cliente' : 'pasada a solo interna'}`;
  }
  //  F11.3: clasificar la gravedad oficial de una NC (NULL = pendiente de clasificar).
  const grav = ev.tipo === 'EDITADA' && ev.sujeto === 'NC' ? ev.cambios?.['gravedad'] : undefined;
  if (grav !== undefined && Object.keys(ev.cambios ?? {}).length === 1) {
    const nombre = (g: unknown): string => typeof g === 'string' ? ETIQUETAS_NIVEL[g] ?? g : '';
    return grav.antes == null
      ? `Gravedad clasificada: ${nombre(grav.despues)}`
      : `Gravedad reclasificada: ${nombre(grav.antes)} → ${nombre(grav.despues)}`;
  }
  const base = ev.sujeto === 'ACCION' ? 'Acción correctiva' : ev.sujeto === 'NC' ? 'No conformidad' : ev.sujeto === 'EVIDENCIA' ? 'Evidencia' : 'Incidencia';
  //  En la línea de tiempo de una NC, las incidencias se nombran por su código.
  const quien = ev.sujeto === 'INCIDENCIA' && sujetoPagina === 'NC' && ev.referencia !== null ? `Incidencia ${ev.referencia}` : base;
  switch (ev.tipo) {
    case 'CREADA':
      return `${quien} creada`;
    case 'ESTADO':
      if (ev.estadoAnterior === null) return `${quien} creada`;
      //  F11.3: cierre y reapertura hechos por el cliente.
      if (ev.sujeto === 'NC' && (ev.cambios as { porCliente?: boolean } | null)?.porCliente === true) {
        if (ev.estadoNuevo === 'CERRADA') return 'El cliente cerró la no conformidad';
        if (ev.estadoAnterior === 'CERRADA') return 'El cliente reabrió la no conformidad';
      }
      if (ev.sujeto === 'NC' && ev.estadoAnterior === 'CERRADA') return 'No conformidad reabierta';
      if (ev.estadoNuevo === 'RESUELTA' && (ev.motivo ?? '').startsWith('Resuelta mediante NC')) return `${quien} resuelta al cerrar la no conformidad`;
      return `${quien}: ${nombreEstado(ev.estadoAnterior)} → ${nombreEstado(ev.estadoNuevo)}`;
    case 'EDITADA':
      return `${quien} editada (${Object.keys(ev.cambios ?? {}).map((k) => NOMBRE_CAMPO[k] ?? k).join(', ')})`;
    case 'ESCALADA':
      return 'Escalada a no conformidad';
    case 'VINCULADA':
      return 'Vinculada a una no conformidad existente';
    case 'SUBIDA':
      return `Evidencia añadida: ${ev.referencia ?? ''}`;
    case 'ANULADA':
      return `Evidencia anulada: ${ev.referencia ?? ''}`;
    default:
      return ev.tipo;
  }
}

export function LineaDeTiempo({ eventos, sujeto }: { eventos: readonly EventoCalidad[]; sujeto?: EventoCalidad['sujeto'] }): ReactNode {
  if (eventos.length === 0) return <p className="pequena tenue">Sin actividad registrada.</p>;
  return (
    <ul className="lista-nc-historial" aria-label="Línea de tiempo">
      {eventos.map((ev) => (
        <li key={ev.id}>
          <span className="pequena">{textoEvento(ev, sujeto)}</span>
          <span className="pequena tenue">
            {' '}
            · {ev.actorNombre ?? 'Sistema'} · {fechaHora(ev.fecha)}
          </span>
          {ev.motivo !== null && ev.motivo !== '' && ev.tipo !== 'SUBIDA' && <div className="pequena suave">{ev.motivo}</div>}
        </li>
      ))}
    </ul>
  );
}

// ------------------------------------------------------------ nueva incidencia

/** Ubicación elegible al dar de alta una incidencia: ruta legible de la raíz a la hoja. */
interface UbicacionIncidencia { id: string; codigo: string; ruta: string }

export interface ContextoIncidencia {
  readonly idCentro?: string;
  readonly idOt?: string;
  readonly idOtLinea?: string;
  /** Líneas elegibles de la OT (para «Reportar incidencia» desde una OT). */
  readonly lineas?: readonly { id: string; etiqueta: string }[];
}

/**
 * Alta de incidencia para quien GESTIONA (ADMIN/CONSULTOR/ENCARGADO). La
 * operaria usa `/reportar` (sin responsable, fecha ni visibilidad — el
 * servidor los rechaza para ella de todas formas).
 */
export function FormularioIncidencia({
  contexto,
  alCrear,
  cancelar,
}: {
  contexto: ContextoIncidencia;
  alCrear: (r: { id: string; codigo: string }) => void;
  cancelar: () => void;
}): ReactNode {
  const idBase = useId();
  const responsables = useResponsables();
  const sinContexto = contexto.idCentro === undefined && contexto.idOt === undefined;
  const clientes = useDatos(
    () => (sinContexto ? api.get<{ clientes: Cliente[] }>('/api/clientes') : Promise.resolve({ clientes: [] as Cliente[] })),
    [sinContexto],
  );
  const [idCliente, setIdCliente] = useState('');
  const centros = useDatos(
    () => (idCliente === '' ? Promise.resolve({ centros: [] as Centro[] }) : api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`)),
    [idCliente],
  );
  const [idCentro, setIdCentroElegido] = useState(contexto.idCentro ?? '');
  //  Pre-F14.2 (0095): ubicación opcional DENTRO del centro; cambiar de centro (o de cliente) la limpia.
  const [idUbicacion, setIdUbicacion] = useState('');
  function setIdCentro(v: string): void {
    setIdCentroElegido(v);
    setIdUbicacion('');
  }
  const ubicaciones = useDatos(
    () => (sinContexto && idCentro !== ''
      ? api.get<{ filas: UbicacionIncidencia[] }>(`/api/calidad/centros/${idCentro}/ubicaciones`)
      : Promise.resolve({ filas: [] as UbicacionIncidencia[] })),
    [sinContexto, idCentro],
  );
  const [idOtLinea, setIdOtLinea] = useState(contexto.idOtLinea ?? '');
  const [descripcion, setDescripcion] = useState('');
  const [gravedad, setGravedad] = useState<GravedadNc>('LEVE');
  const [idResponsable, setIdResponsable] = useState('');
  const [fechaObjetivo, setFechaObjetivo] = useState('');
  const [visibleCliente, setVisibleCliente] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    if (descripcion.trim().length < 5) {
      setError(new globalThis.Error('Describe qué ha pasado (al menos 5 caracteres).'));
      return;
    }
    if (contexto.idOt === undefined && idCentro === '') {
      setError(new globalThis.Error('Elige el centro.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const r = await api.post<{ id: string; codigo: string }>('/api/incidencias', {
        ...(contexto.idOt !== undefined ? { idOt: contexto.idOt } : { idCentro }),
        ...(idOtLinea !== '' ? { idOtLinea } : {}),
        ...(idUbicacion !== '' ? { idUbicacion } : {}),
        descripcion: descripcion.trim(),
        gravedad,
        ...(idResponsable !== '' ? { idResponsable } : {}),
        ...(fechaObjetivo !== '' ? { fechaObjetivo } : {}),
        visibleCliente,
      });
      alCrear(r);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera">
        <h3>{contexto.idOt !== undefined ? 'Reportar incidencia' : 'Nueva incidencia'}</h3>
        <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        {sinContexto && (
          <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-cli`} className="obligatorio">Cliente</label>
              <select id={`${idBase}-cli`} required value={idCliente} onChange={(e) => { setIdCliente(e.target.value); setIdCentro(''); }}>
                <option value="">Elige cliente…</option>
                {(clientes.datos?.clientes ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.razonSocial}</option>
                ))}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-cen`} className="obligatorio">Centro</label>
              <select id={`${idBase}-cen`} required value={idCentro} onChange={(e) => setIdCentro(e.target.value)} disabled={idCliente === ''}>
                <option value="">Elige centro…</option>
                {(centros.datos?.centros ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.descripcion}</option>
                ))}
              </select>
            </div>
            <div className="campo" style={{ margin: 0 }}>
              <label htmlFor={`${idBase}-ubi`}>Ubicación (opcional)</label>
              <select id={`${idBase}-ubi`} value={idUbicacion} onChange={(e) => setIdUbicacion(e.target.value)} disabled={idCentro === ''}>
                <option value="">Sin ubicación concreta</option>
                {(ubicaciones.datos?.filas ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{u.ruta}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {contexto.lineas !== undefined && contexto.lineas.length > 0 && (
          <div className="campo">
            <label htmlFor={`${idBase}-lin`}>Línea (opcional)</label>
            <select id={`${idBase}-lin`} value={idOtLinea} onChange={(e) => setIdOtLinea(e.target.value)}>
              <option value="">Toda la OT</option>
              {contexto.lineas.map((l) => (
                <option key={l.id} value={l.id}>{l.etiqueta}</option>
              ))}
            </select>
          </div>
        )}
        <div className="campo">
          <label htmlFor={`${idBase}-desc`}>¿Qué ha pasado?</label>
          <textarea id={`${idBase}-desc`} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3} />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-niv`}>Nivel</label>
            <select id={`${idBase}-niv`} value={gravedad} onChange={(e) => setGravedad(e.target.value as GravedadNc)}>
              <option value="LEVE">Leve</option>
              <option value="GRAVE">Grave</option>
              <option value="CRITICA">Crítica</option>
            </select>
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-resp`}>Responsable</label>
            <SelectorResponsable id={`${idBase}-resp`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-fec`}>Fecha objetivo</label>
            <input id={`${idBase}-fec`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
          </div>
        </div>
        <label className="fila" style={{ gap: 6 }}>
          <input type="checkbox" checked={visibleCliente} onChange={(e) => setVisibleCliente(e.target.checked)} />
          Visible para el cliente
        </label>
        <div className="sep">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
            {enviando ? 'Guardando…' : 'Registrar incidencia'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ incidencias de una OT

/** Enlace «Reportar incidencia» en cada línea: abre el formulario de la sección de la OT con la línea ya elegida. */
export function BotonReportarIncidenciaLinea({ idLinea }: { idLinea: string }): ReactNode {
  const { puede } = useSesion();
  if (!puede('incidencias:crear')) return null;
  return (
    <Link to={`?reportarIncidencia=${idLinea}#incidencias`} className="etiqueta neutra" aria-label="Reportar incidencia de esta línea">
      Reportar incidencia
    </Link>
  );
}

export function SeccionIncidenciasOt({ ot }: { ot: OtDetalle }): ReactNode {
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const lineaPedida = params.get('reportarIncidencia');
  const puedeLeer = puede('incidencias:leer') && puede('incidencias:crear');
  const lista = useDatos(
    () => (puedeLeer ? api.get<PaginaIncidencias>(`/api/incidencias?idOt=${ot.id}&porPagina=100`) : Promise.resolve(null)),
    [ot.id, puedeLeer],
  );
  const [creando, setCreando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lineaPedida !== null && puedeLeer) {
      setCreando(true);
      setMensaje(null);
      ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [lineaPedida, puedeLeer]);

  if (!puedeLeer) return null;

  function cerrarFormulario(): void {
    setCreando(false);
    if (lineaPedida !== null) {
      params.delete('reportarIncidencia');
      setParams(params, { replace: true });
    }
  }

  return (
    <div className="panel" id="incidencias" ref={ref}>
      <div className="panel-cabecera">
        <h2>Incidencias</h2>
        {!creando && (
          <button type="button" className="pequeno" onClick={() => { setCreando(true); setMensaje(null); }}>
            Reportar incidencia
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        {mensaje !== null && !creando && <div className="nota exito" role="status">{mensaje}</div>}
        {creando && (
          <FormularioIncidencia
            key={lineaPedida ?? 'ot'}
            contexto={{
              idOt: ot.id,
              ...(lineaPedida !== null ? { idOtLinea: lineaPedida } : {}),
              lineas: ot.lineas.map((l) => ({ id: l.id, etiqueta: `${l.ubicacionCodigo} · ${l.descripcionTarea ?? l.tipoDescripcion}` })),
            }}
            cancelar={cerrarFormulario}
            alCrear={(r) => {
              cerrarFormulario();
              setMensaje(`Incidencia ${r.codigo} registrada.`);
              lista.recargar();
            }}
          />
        )}
        {lista.cargando && lista.datos === null && <Cargando texto="Cargando incidencias…" />}
        {lista.error !== null && <ErrorCaja error={lista.error} reintentar={lista.recargar} />}
        {lista.datos !== null &&
          (lista.datos.filas.length === 0 ? (
            <Vacio titulo="Sin incidencias en esta OT" />
          ) : (
            <ul className="lista-nc">
              {lista.datos.filas.map((f) => <TarjetaIncidencia key={f.id} f={f} />)}
            </ul>
          ))}
      </div>
    </div>
  );
}

export function TarjetaIncidencia({ f, a }: { f: IncidenciaFila; a?: string }): ReactNode {
  return (
    <li className="nc-tarjeta">
      <Link to={a ?? `/calidad/incidencias/${f.id}`} className="nc-tarjeta-cabecera" style={{ textDecoration: 'none' }}>
        <span className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <span className="mono">{f.codigo}</span>
          <EstadoIncidenciaEtiqueta estado={f.estado} />
          <NivelEtiqueta nivel={f.gravedad} />
          {f.vencida && <span className="etiqueta error">Vencida</span>}
          {f.codigoNc !== null && <span className="etiqueta info">{f.codigoNc}</span>}
        </span>
        <span className="pequena suave">{fechaHora(f.creadoEn)}</span>
      </Link>
      <div className="nc-tarjeta-resumen">
        <p>{f.descripcion}</p>
        <p className="pequena tenue">
          {f.centroNombre}
          {f.ubicacion != null && ` › ${f.ubicacion}`}
          {f.otCodigo !== null && ` · ${f.otCodigo}`}
          {f.lineaDescripcion !== null && ` · ${f.lineaDescripcion}`}
          {f.responsableNombre !== null && ` · Responsable: ${f.responsableNombre}`}
          {f.fechaObjetivo !== null && ` · Objetivo: ${fecha(f.fechaObjetivo)}`}
        </p>
      </div>
    </li>
  );
}

// ------------------------------------------------------------ ficha de calidad de una NC

/**
 * Lo que 7.1 añade a una NC: análisis de causa, responsable/fecha objetivo,
 * visibilidad para el cliente, acciones correctivas e incidencias que la
 * originaron. F11.1: la ficha la pide `DetalleNc` UNA vez (también alimenta
 * el cierre guiado y el historial único); aquí sólo se pinta y se edita.
 */
export function FichaCalidadNcSeccion({
  idNc,
  estadoNc,
  origenNc,
  ficha: d,
  onCambiada,
}: {
  idNc: string;
  estadoNc: string;
  origenNc: string;
  ficha: FichaCalidadNc;
  onCambiada: () => void;
}): ReactNode {
  const { puede } = useSesion();
  const responsables = useResponsables();
  const abierta = estadoNc === 'ABIERTA' || estadoNc === 'EN_TRATAMIENTO';
  const puedeTratar = puede('nc:tratar');
  const alCambiar = onCambiada;

  return (
    <div className="sep">
      <EditorFichaNc idNc={idNc} ficha={d} origenNc={origenNc} abierta={abierta} editable={puedeTratar} responsables={responsables} alGuardar={alCambiar} />

      {d.incidencias.length > 0 && (
        <div className="sep">
          <div className="etiqueta-dato">Incidencias vinculadas ({d.incidencias.length})</div>
          <ul className="lista-nc-lineas">
            {d.incidencias.map((i) => (
              <li key={i.id}>
                <Link to={`/calidad/incidencias/${i.id}`} className="mono pequena">{i.codigo}</Link>{' '}
                <EstadoIncidenciaEtiqueta estado={i.estado} /> <span className="pequena tenue">{i.descripcion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <AccionesCorrectivas idNc={idNc} acciones={d.acciones} abierta={abierta} editable={puedeTratar} responsables={responsables} alCambiar={alCambiar} />

      <ComentariosNc idNc={idNc} comentarios={d.comentarios ?? []} visibleCliente={d.visibleCliente} anulada={estadoNc === 'ANULADA'} alCambiar={alCambiar} />
    </div>
  );
}

// ------------------------------------------------------------ comentarios (F11.3)

/**
 * La conversación de la NC tal y como la ve gestión. Dos botones con nombre
 * propio — nunca una casilla ambigua — porque el público de un comentario no
 * se puede cambiar después (append-only, 0078).
 */
function ComentariosNc({
  idNc,
  comentarios,
  visibleCliente,
  anulada,
  alCambiar,
}: {
  idNc: string;
  comentarios: readonly ComentarioNc[];
  visibleCliente: boolean;
  anulada: boolean;
  alCambiar: () => void;
}): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const [modo, setModo] = useState<'INTERNO' | 'CLIENTE' | null>(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const puedeComentar = puede('nc:comentar') && !anulada;

  async function enviar(): Promise<void> {
    if (texto.trim() === '') {
      setError(new globalThis.Error('Escribe el comentario.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/no-conformidades/${idNc}/comentarios`, { texto: texto.trim(), visibleCliente: modo === 'CLIENTE' });
      setTexto('');
      setModo(null);
      alCambiar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="sep" role="region" aria-label="Comentarios">
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h3 className="titulo-seccion-nc">Comentarios ({comentarios.length})</h3>
        {puedeComentar && modo === null && (
          <div className="lista-checks-horizontal">
            <button type="button" className="pequeno" onClick={() => setModo('INTERNO')}>Comentario interno</button>
            <button
              type="button"
              className="pequeno"
              disabled={!visibleCliente}
              title={visibleCliente ? undefined : 'Publica la NC al cliente antes de escribirle.'}
              onClick={() => setModo('CLIENTE')}
            >
              Actualización para el cliente
            </button>
          </div>
        )}
      </div>
      {modo !== null && (
        <div className={`panel accion-linea ${modo === 'CLIENTE' ? 'comentario-publico' : ''}`}>
          <div className="panel-cuerpo">
            <h4 style={{ margin: '0 0 4px' }}>{modo === 'CLIENTE' ? 'Actualización para el cliente' : 'Comentario interno'}</h4>
            <p className="pequena suave" style={{ marginTop: 0 }}>
              {modo === 'CLIENTE'
                ? 'El cliente lo verá en su portal, firmado como «Equipo de servicio». No se puede editar ni retirar después.'
                : 'Solo lo verá la empresa. No se puede editar ni borrar después.'}
            </p>
            {error !== null && <ErrorCaja error={error} />}
            <div className="campo">
              <label htmlFor={`${idBase}-t`}>{modo === 'CLIENTE' ? 'Texto para el cliente' : 'Texto interno'}</label>
              <textarea id={`${idBase}-t`} rows={3} value={texto} onChange={(e) => setTexto(e.target.value)} />
            </div>
            <div className="lista-checks-horizontal">
              <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
                {enviando ? 'Enviando…' : modo === 'CLIENTE' ? 'Enviar al cliente' : 'Guardar comentario interno'}
              </button>
              <button type="button" className="pequeno" disabled={enviando} onClick={() => { setModo(null); setError(null); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
      {comentarios.length === 0 ? (
        <p className="pequena tenue">Todavía no hay comentarios.</p>
      ) : (
        <ul className="lista-nc-historial" aria-label="Lista de comentarios">
          {comentarios.map((k) => (
            <li key={k.id} className={k.deCliente ? 'comentario-cliente' : undefined}>
              <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
                <span className={`etiqueta ${k.deCliente ? 'aviso' : k.visibleCliente ? 'info' : 'neutra'}`}>
                  {k.deCliente ? 'Del cliente' : k.visibleCliente ? 'Visible para el cliente' : 'Interno'}
                </span>
                <span className="pequena tenue">{k.autorNombre ?? '—'} · {fechaHora(k.creadoEn)}</span>
              </div>
              <p className="pequena" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{k.texto}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------ clasificación (F11.3)

/**
 * La gravedad OFICIAL la fija la empresa. Una NC del cliente nace «pendiente de
 * clasificar» (no es un estado); clasificar y reclasificar quedan auditados.
 */
export function ClasificacionNc({ idNc, gravedad, alCambiar }: { idNc: string; gravedad: string | null; alCambiar: () => void }): ReactNode {
  const { puede } = useSesion();
  const idBase = useId();
  const [valor, setValor] = useState(gravedad ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!puede('nc:tratar')) return null;

  async function guardar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.patch(`/api/no-conformidades/${idNc}`, { gravedad: valor });
      alCambiar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className={`sep ${gravedad === null ? 'nota aviso' : ''}`} role="region" aria-label="Gravedad oficial">
      {gravedad === null && <strong>Pendiente de clasificar: la gravedad oficial la decide la empresa.</strong>}
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila filtros-calidad" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="campo" style={{ margin: 0 }}>
          <label htmlFor={`${idBase}-g`}>Gravedad oficial</label>
          <select id={`${idBase}-g`} value={valor} onChange={(e) => setValor(e.target.value)}>
            {gravedad === null && <option value="">Elige…</option>}
            <option value="LEVE">Leve</option>
            <option value="GRAVE">Grave</option>
            <option value="CRITICA">Crítica</option>
          </select>
        </div>
        <button type="button" className={gravedad === null ? 'principal' : 'pequeno'} disabled={enviando || valor === '' || valor === gravedad} onClick={() => void guardar()}>
          {enviando ? 'Guardando…' : gravedad === null ? 'Clasificar' : 'Reclasificar'}
        </button>
      </div>
    </div>
  );
}

function EditorFichaNc({
  idNc,
  ficha,
  origenNc,
  abierta,
  editable,
  responsables,
  alGuardar,
}: {
  idNc: string;
  ficha: FichaCalidadNc;
  origenNc: string;
  abierta: boolean;
  editable: boolean;
  responsables: readonly ResponsableCandidato[];
  alGuardar: () => void;
}): ReactNode {
  const idBase = useId();
  const [editando, setEditando] = useState(false);
  const [analisis, setAnalisis] = useState(ficha.analisisCausa ?? '');
  const [fechaObjetivo, setFechaObjetivo] = useState(ficha.fechaObjetivo ?? '');
  const [idResponsable, setIdResponsable] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmandoVisibilidad, setConfirmandoVisibilidad] = useState(false);
  //  Reflejo de la regla de la base (F11.2, NC_CON_PARTICIPACION_CLIENTE): si el cliente participó (la abrió,
  //  comentó o aportó evidencia), ya no vuelve a ser sólo interna. El dato lo da el servidor.
  const siempreVisible = ficha.visibleCliente && (ficha.participacionCliente === true || origenNc === 'CLIENTE');

  async function guardar(cambios: Record<string, unknown>): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.patch(`/api/no-conformidades/${idNc}`, cambios);
      setEditando(false);
      setConfirmandoVisibilidad(false);
      alGuardar();
    } catch (e) {
      setConfirmandoVisibilidad(false);
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="sep">
      {error !== null && <ErrorCaja error={error} />}
      {!editando ? (
        <>
          <div className="datos">
            <div className="dato">
              <div className="etiqueta-dato">Análisis de causa</div>
              <div className="valor-dato">{ficha.analisisCausa ?? '—'}</div>
            </div>
          </div>
          {siempreVisible && (
            <p className="pequena suave">
              {origenNc === 'CLIENTE' ? 'La abrió el cliente' : 'El cliente ya ha participado'}: siempre es visible para él.
            </p>
          )}
          {editable && (
            <div className="lista-checks-horizontal">
              {abierta && (
                <button type="button" className="pequeno" onClick={() => setEditando(true)}>
                  Editar análisis y seguimiento
                </button>
              )}
              {!siempreVisible && (
                <button type="button" className="pequeno" disabled={enviando} onClick={() => setConfirmandoVisibilidad(true)}>
                  {ficha.visibleCliente ? 'Pasar la NC a solo interna' : 'Publicar la NC al cliente'}
                </button>
              )}
            </div>
          )}
          {/*  F11.1: publicar u ocultar una NC entera es un cambio sensible: siempre se confirma. */}
          {confirmandoVisibilidad && (
            ficha.visibleCliente ? (
              <ConfirmDialog
                titulo="¿Pasar la NC a solo interna?"
                descripcion="El cliente dejará de ver esta no conformidad, su evolución y las evidencias compartidas con él."
                confirmar="Pasar a solo interna"
                ocupado={enviando}
                alConfirmar={() => guardar({ visibleCliente: false })}
                cerrar={() => setConfirmandoVisibilidad(false)}
              />
            ) : (
              <ConfirmDialog
                titulo="¿Publicar la NC al cliente?"
                descripcion="El cliente verá la descripción, el centro y la OT, el estado, la fecha de compromiso, la resolución al cerrar y las evidencias compartidas con él."
                consecuencia="No verá el análisis de causa, las acciones correctivas, los responsables ni las evidencias internas."
                confirmar="Publicar al cliente"
                variante="principal"
                ocupado={enviando}
                alConfirmar={() => guardar({ visibleCliente: true })}
                cerrar={() => setConfirmandoVisibilidad(false)}
              />
            )
          )}
        </>
      ) : (
        <div className="panel accion-linea">
          <div className="panel-cuerpo">
            <div className="campo">
              <label htmlFor={`${idBase}-an`}>Análisis de causa</label>
              <textarea id={`${idBase}-an`} rows={3} value={analisis} onChange={(e) => setAnalisis(e.target.value)} />
            </div>
            <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-resp`}>Responsable</label>
                <SelectorResponsable id={`${idBase}-resp`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} vacio="Sin cambios" />
              </div>
              <div className="campo" style={{ margin: 0 }}>
                <label htmlFor={`${idBase}-fec`}>Fecha objetivo</label>
                <input id={`${idBase}-fec`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
              </div>
            </div>
            <div className="lista-checks-horizontal">
              <button
                type="button"
                className="principal"
                disabled={enviando}
                onClick={() =>
                  void guardar({
                    analisisCausa: analisis.trim() === '' ? null : analisis.trim(),
                    fechaObjetivo: fechaObjetivo === '' ? null : fechaObjetivo,
                    ...(idResponsable !== '' ? { idResponsable } : {}),
                  })
                }
              >
                {enviando ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" className="pequeno" onClick={() => setEditando(false)} disabled={enviando}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AccionesCorrectivas({
  idNc,
  acciones,
  abierta,
  editable,
  responsables,
  alCambiar,
}: {
  idNc: string;
  acciones: readonly AccionCorrectiva[];
  abierta: boolean;
  editable: boolean;
  responsables: readonly ResponsableCandidato[];
  alCambiar: () => void;
}): ReactNode {
  const [creando, setCreando] = useState(false);
  return (
    <div className="sep">
      <div className="fila" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div className="etiqueta-dato">Acciones correctivas ({acciones.length})</div>
        {abierta && editable && !creando && (
          <button type="button" className="pequeno" onClick={() => setCreando(true)}>
            Nueva acción correctiva
          </button>
        )}
      </div>
      {creando && (
        <FormularioAccion
          idNc={idNc}
          responsables={responsables}
          cancelar={() => setCreando(false)}
          alCrear={() => {
            setCreando(false);
            alCambiar();
          }}
        />
      )}
      {acciones.length === 0 ? (
        <p className="pequena tenue">Todavía no hay acciones correctivas.</p>
      ) : (
        <ul className="lista-nc">
          {acciones.map((a) => (
            <TarjetaAccion key={a.id} accion={a} modo={editable && abierta ? 'gestion' : 'lectura'} responsables={responsables} alCambiar={alCambiar} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FormularioAccion({
  idNc,
  responsables,
  cancelar,
  alCrear,
}: {
  idNc: string;
  responsables: readonly ResponsableCandidato[];
  cancelar: () => void;
  alCrear: () => void;
}): ReactNode {
  const idBase = useId();
  const [descripcion, setDescripcion] = useState('');
  const [idResponsable, setIdResponsable] = useState('');
  const [fechaObjetivo, setFechaObjetivo] = useState('');
  const [visibleCliente, setVisibleCliente] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function enviar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/no-conformidades/${idNc}/acciones`, { descripcion: descripcion.trim(), idResponsable, fechaObjetivo, visibleCliente });
      alCrear();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-desc`}>¿Qué se va a hacer?</label>
          <textarea
            id={`${idBase}-desc`}
            rows={2}
            value={descripcion}
            placeholder="Ej.: Sustituir el dispensador y revisar los del resto de aseos"
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-resp`}>Responsable</label>
            <SelectorResponsable id={`${idBase}-resp`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} vacio={null} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-fec`}>Fecha objetivo</label>
            <input id={`${idBase}-fec`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
          </div>
        </div>
        {/*  F11.3: visibilidad elegida a propósito, con dos opciones con nombre — el cliente nunca ve responsable ni plazo. */}
        <fieldset className="campo filtros-calidad" style={{ border: 0, padding: 0 }}>
          <legend className="etiqueta-dato">Visibilidad</legend>
          <div className="lista-checks-horizontal">
            <label className="fila" style={{ gap: 6 }}>
              <input type="radio" name={`${idBase}-vis`} checked={!visibleCliente} onChange={() => setVisibleCliente(false)} />
              Solo interna
            </label>
            <label className="fila" style={{ gap: 6 }}>
              <input type="radio" name={`${idBase}-vis`} checked={visibleCliente} onChange={() => setVisibleCliente(true)} />
              Visible para cliente
            </label>
          </div>
          {visibleCliente && <p className="pequena suave" style={{ margin: 0 }}>El cliente verá la descripción, el estado y, al completarla, el resultado. Nunca el responsable ni el plazo.</p>}
        </fieldset>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void enviar()}>
            {enviando ? 'Guardando…' : 'Añadir acción'}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Una acción correctiva, operable donde esté (ficha de NC, bandeja
 * «Acciones pendientes» o «Mis acciones»). FASE 7.2:
 *   · 'gestion'     — `nc:tratar`: todas las transiciones y editar.
 *   · 'responsable' — sólo su responsable: empezar y completar (con
 *                     resultado) y adjuntar evidencia; nada más.
 *   · 'lectura'     — ninguna acción.
 * El servidor revalida siempre; esto sólo decide qué botones mostrar.
 */
export function TarjetaAccion({
  accion,
  modo,
  responsables = [],
  mostrarNc = false,
  alCambiar,
}: {
  accion: AccionCorrectiva;
  modo: 'gestion' | 'responsable' | 'lectura';
  responsables?: readonly ResponsableCandidato[];
  mostrarNc?: boolean;
  alCambiar: () => void;
}): ReactNode {
  const idBase = useId();
  const [destino, setDestino] = useState<EstadoAccionCorrectiva | null>(null);
  const [texto, setTexto] = useState('');
  const [verEvidencias, setVerEvidencias] = useState(false);
  const [editando, setEditando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmandoVisibilidad, setConfirmandoVisibilidad] = useState(false);
  const viva = accion.estado === 'PENDIENTE' || accion.estado === 'EN_CURSO';

  async function cambiarVisibilidad(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.patch(`/api/acciones-correctivas/${accion.id}`, { visibleCliente: accion.visibleCliente !== true });
      setConfirmandoVisibilidad(false);
      alCambiar();
    } catch (e) {
      setConfirmandoVisibilidad(false);
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  const siguientes: readonly EstadoAccionCorrectiva[] =
    modo === 'gestion' ? SIGUIENTES_ACCION[accion.estado]
      : modo === 'responsable' && viva ? SIGUIENTES_ACCION[accion.estado].filter((e) => e === 'EN_CURSO' || e === 'COMPLETADA')
        : [];
  const pideTexto = destino === 'COMPLETADA' || destino === 'CANCELADA';

  async function aplicar(estado: EstadoAccionCorrectiva, t: string): Promise<void> {
    if (estado === 'COMPLETADA' && t.trim().length < 5) {
      setError(new globalThis.Error('Describe el resultado obtenido (al menos 5 caracteres).'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/acciones-correctivas/${accion.id}/estado`, {
        estado,
        ...(estado === 'COMPLETADA' ? { resultado: t.trim() } : {}),
        ...(estado === 'CANCELADA' ? { motivo: t.trim() } : {}),
      });
      setDestino(null);
      setTexto('');
      alCambiar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <li className="nc-tarjeta">
      <div className="nc-tarjeta-resumen">
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <EstadoAccionEtiqueta estado={accion.estado} />
          {accion.vencida && <span className="etiqueta error">Vencida</span>}
          {accion.visibleCliente !== undefined && (
            <span className={`etiqueta ${accion.visibleCliente ? 'info' : 'neutra'}`}>{accion.visibleCliente ? 'Visible para cliente' : 'Solo interna'}</span>
          )}
          {mostrarNc && (
            <Link to={`/calidad/no-conformidades/${accion.idNc}`} className="mono">{accion.codigoNc}</Link>
          )}
          <span className="pequena tenue">
            Responsable: {accion.responsableNombre ?? '—'} · Plazo: {fecha(accion.fechaObjetivo)}
            {mostrarNc ? ` · ${accion.centroNombre}` : ''}
          </span>
        </div>
        <p>{accion.descripcion}</p>
        {accion.completadaEn !== null && accion.estado === 'COMPLETADA' && (
          <p className="pequena tenue">
            Completada el {fechaHora(accion.completadaEn)}{accion.completadaPorNombre !== null ? ` por ${accion.completadaPorNombre}` : ''}
          </p>
        )}
        {accion.resultado !== null && <p className="pequena suave">Resultado: {accion.resultado}</p>}
        {modo === 'lectura' && accion.evidencias > 0 && (
          <p className="pequena tenue">Evidencias de la acción: {accion.evidencias}</p>
        )}
        {accion.motivoCancelacion !== null && <p className="pequena suave">Cancelada: {accion.motivoCancelacion}</p>}
        {error !== null && <ErrorCaja error={error} />}
        {editando ? (
          <EditorAccion accion={accion} responsables={responsables} cancelar={() => setEditando(false)} alGuardar={() => { setEditando(false); alCambiar(); }} />
        ) : destino !== null && pideTexto ? (
          <div className="campo">
            <label htmlFor={`${idBase}-t`}>{destino === 'COMPLETADA' ? 'Resultado obtenido' : 'Motivo de la cancelación'}</label>
            <textarea id={`${idBase}-t`} rows={2} value={texto} onChange={(e) => setTexto(e.target.value)} />
            <div className="lista-checks-horizontal">
              <button type="button" className="principal" disabled={enviando} onClick={() => void aplicar(destino, texto)}>
                {enviando ? 'Guardando…' : VERBO_ACCION[destino]}
              </button>
              <button type="button" className="pequeno" onClick={() => setDestino(null)} disabled={enviando}>
                Volver
              </button>
            </div>
          </div>
        ) : (
          <div className="lista-checks-horizontal">
            {siguientes.map((s) => (
              <button
                key={s}
                type="button"
                className="pequeno"
                disabled={enviando}
                onClick={() => (s === 'COMPLETADA' || s === 'CANCELADA' ? setDestino(s) : void aplicar(s, ''))}
              >
                {s === 'EN_CURSO' && accion.estado === 'COMPLETADA' ? 'Reabrir' : VERBO_ACCION[s]}
              </button>
            ))}
            {modo === 'gestion' && viva && (
              <button type="button" className="pequeno" onClick={() => setEditando(true)}>
                Editar
              </button>
            )}
            {modo === 'gestion' && accion.estado !== 'CANCELADA' && accion.visibleCliente !== undefined && (
              <button type="button" className="pequeno" disabled={enviando} onClick={() => setConfirmandoVisibilidad(true)}>
                {accion.visibleCliente ? 'Dejar solo interna' : 'Compartir con el cliente'}
              </button>
            )}
            {modo !== 'lectura' && (
              <button type="button" className="pequeno" onClick={() => setVerEvidencias((v) => !v)} aria-expanded={verEvidencias}>
                Evidencias{accion.evidencias > 0 ? ` (${String(accion.evidencias)})` : ''}
              </button>
            )}
          </div>
        )}
        {confirmandoVisibilidad && (
          <ConfirmDialog
            titulo={accion.visibleCliente === true ? '¿Dejar la acción solo interna?' : '¿Compartir la acción con el cliente?'}
            descripcion={accion.visibleCliente === true
              ? 'El cliente dejará de ver esta acción y sus evidencias.'
              : 'El cliente verá la descripción, el estado y, al completarla, el resultado y las evidencias que compartas. Nunca el responsable ni el plazo.'}
            confirmar={accion.visibleCliente === true ? 'Dejar solo interna' : 'Compartir con el cliente'}
            variante={accion.visibleCliente === true ? 'peligro' : 'principal'}
            ocupado={enviando}
            alConfirmar={() => cambiarVisibilidad()}
            cerrar={() => setConfirmandoVisibilidad(false)}
          />
        )}
        {verEvidencias && (
          <Evidencias
            ambito="ACCION"
            ruta={`/api/acciones-correctivas/${accion.id}/evidencias`}
            titulo="Evidencias de la acción"
            accesoPropio={modo === 'responsable'}
          />
        )}
      </div>
    </li>
  );
}

/** FASE 7.2 — edición por gestión: descripción, responsable y fecha. Nunca resultado, historial ni actores. */
function EditorAccion({
  accion,
  responsables,
  cancelar,
  alGuardar,
}: {
  accion: AccionCorrectiva;
  responsables: readonly ResponsableCandidato[];
  cancelar: () => void;
  alGuardar: () => void;
}): ReactNode {
  const idBase = useId();
  const [descripcion, setDescripcion] = useState(accion.descripcion);
  const [idResponsable, setIdResponsable] = useState(accion.idResponsable);
  const [fechaObjetivo, setFechaObjetivo] = useState(accion.fechaObjetivo);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function guardar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.patch(`/api/acciones-correctivas/${accion.id}`, { descripcion: descripcion.trim(), idResponsable, fechaObjetivo });
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <div className="panel accion-linea">
      <div className="panel-cuerpo">
        {error !== null && <ErrorCaja error={error} />}
        <div className="campo">
          <label htmlFor={`${idBase}-d`}>Qué se va a hacer</label>
          <textarea id={`${idBase}-d`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </div>
        <div className="fila" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-r`}>Responsable</label>
            <SelectorResponsable id={`${idBase}-r`} valor={idResponsable} onChange={setIdResponsable} responsables={responsables} vacio={null} />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label htmlFor={`${idBase}-f`}>Fecha objetivo</label>
            <input id={`${idBase}-f`} type="date" value={fechaObjetivo} onChange={(e) => setFechaObjetivo(e.target.value)} />
          </div>
        </div>
        <div className="lista-checks-horizontal">
          <button type="button" className="principal" disabled={enviando} onClick={() => void guardar()}>
            {enviando ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <button type="button" className="pequeno" onClick={cancelar} disabled={enviando}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
